import React, { useMemo, useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { RootState } from "../../../store/store";
import "../styles/GalleryStyles.css";
import VideoRecorder from "../components/VideoRecorder";
import ImageCapture from "../components/ImageRecorder";
import ScreenRecorder from "../components/ScreenRecorder";
import { useApi } from "../../../service/ApiContext";
import { useDispatch } from "react-redux";
import dropdownArrow from "../../../assets/images/Profile_Img/arrowDropDown.png";
import Loader, { LocalStorage } from "../../../utils/Utils";
import Logger from "../../../utils/Logger";
import { setPrograms } from "../../liveSessions/data/reducer/ProgramDetailReducer";
import { setSelectedValues } from "../../liveSessions/data/reducer/eventReducer";
import { useAlert } from "../../../components/AlertContextComponent";
import CommonUtils from "../../../utils/Common_Utils";
import { Tooltip } from "@mui/material";
import backArrowImg from "../../recommendation/assets/images/common/recommand_right-arrow.png";

export type MediaType = "image" | "video";

export interface GalleryMedia {
  id: string;
  type: MediaType;
  url: string; // local blob URL for instant UI preview
  s3Url: string; // Simulated S3 pre-signed URL
  fileName: string;
  createdAt: string; // ISO String
  author: string; // Captured by
}

interface GalleryResponse {
  media: GalleryItem[];
}

interface GalleryItem {
  candidate_id: string;
  institute_id: string;
  gallery_id: string;
  uploaded_by: string;
  s3_key: string;
  uploaded_at: string;
  media_type: "image" | "video";
  media_url: string;
  file_name: string;
  uploaded_by_user: boolean;
  feedback_message?: string;
}

interface EllipsisTooltipProps {
  text: string;
  className?: string;
}

const EllipsisTooltip: React.FC<EllipsisTooltipProps> = ({
  text,
  className,
}) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    const el = textRef.current;
    if (el) {
      setShowTooltip(el.scrollWidth > el.clientWidth);
    }
  }, [text]);

  return (
    <Tooltip title={showTooltip ? text : ""} arrow>
      <span ref={textRef} className={className}>
        {text}
      </span>
    </Tooltip>
  );
};

const GalleryPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const navState = location.state as {
    fromLearningRoomVideo?: boolean;
    activeTab?: "gallery" | "capture";
    program_id?: string;
    programId?: string;
    subject_id?: string;
    subjectId?: string;
    course_id?: string;
    topic_id?: string;
    topicId?: string;
    video_id?: string;
    videoId?: string;
    program_name?: string;
    programName?: string;
    subject_name?: string;
    subjectName?: string;
    course_name?: string;
    topic_name?: string;
    topicName?: string;
    video_name?: string;
    videoName?: string;
  } | null;

  const [activeTab, setActiveTab] = useState<"gallery" | "capture">(
    navState?.fromLearningRoomVideo || navState?.activeTab === "capture"
      ? "capture"
      : "gallery",
  );
  const [captureMode, setCaptureMode] = useState<"image" | "video" | "screen">(
    "image",
  );
  const dispatch = useDispatch();
  const programs = useSelector(
    (state: RootState) => state.events.selectedValues,
  );
  const { showAlert } = useAlert();
  const selectedProgramId = programs?.[0]?.program_id || "";
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [filter, setFilter] = useState("All Programs");

  const listProgramData = useSelector(
    (state: RootState) => state.programs.programs,
  );

  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState("");

  // 4-Level Hierarchy States
  const [selectedProgramState, setSelectedProgramState] = useState<{
    id: string;
    title: string;
  }>({ id: "", title: "" });

  const [subjectsList, setSubjectsList] = useState<
    { course_id: string; course_title: string }[]
  >([]);
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState<{
    id: string;
    title: string;
  }>({ id: "", title: "" });

  const [topicsList, setTopicsList] = useState<
    { topic_id: string; topic_title: string }[]
  >([]);
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState<{
    id: string;
    title: string;
  }>({ id: "", title: "" });

  const [videosList, setVideosList] = useState<
    { video_id: string; video_title: string }[]
  >([]);
  const [loadingVideos, setLoadingVideos] = useState(false);
  const [selectedVideo, setSelectedVideoState] = useState<{
    id: string;
    title: string;
  }>({ id: "", title: "" });

  const [openHierarchyDrop, setOpenHierarchyDrop] = useState<
    "program" | "subject" | "topic" | "video" | null
  >(null);

  // Fetch Subjects for a selected Program
  const fetchSubjects = async (programId: string) => {
    if (!programId) return [];
    setLoadingSubjects(true);
    api.setAuthToken(LocalStorage.getLocalData("access_token"));
    try {
      const response = await api.get<any>(
        `/assessment/recommendation?recommendation_type=Industry&program_id=${programId}`,
      );
      const recs = response.data?.recommendation || [];
      const mapped = recs.map((item: any) => ({
        course_id: item.course_id,
        course_title: item.course_title,
      }));
      setSubjectsList(mapped);
      return mapped;
    } catch (error) {
      Logger.error("Error fetching subjects for gallery:", error);
      setSubjectsList([]);
      return [];
    } finally {
      setLoadingSubjects(false);
    }
  };

  // Fetch Topics for a selected Subject
  const fetchTopics = async (programId: string, courseId: string) => {
    if (!programId || !courseId) return [];
    setLoadingTopics(true);
    api.setAuthToken(LocalStorage.getLocalData("access_token"));
    try {
      const response = await api.get<any>(
        `/candidate/list_topics?course_id=${courseId}&program_id=${programId}&program_category=Industry`,
      );
      const topics = response.data?.list_topics || [];
      const mapped = topics.map((t: any) => ({
        topic_id: t.topic_id,
        topic_title: t.topic_title,
      }));
      setTopicsList(mapped);
      return mapped;
    } catch (error) {
      Logger.error("Error fetching topics for gallery:", error);
      setTopicsList([]);
      return [];
    } finally {
      setLoadingTopics(false);
    }
  };

  // Fetch Videos for a selected Topic
  const fetchVideos = async (
    programId: string,
    courseId: string,
    topicId: string,
  ) => {
    if (!programId || !courseId || !topicId) return [];
    setLoadingVideos(true);
    api.setAuthToken(LocalStorage.getLocalData("access_token"));
    try {
      const response = await api.get<any>(
        `/candidate/list_videos?course_id=${courseId}&topic_id=${topicId}&program_id=${programId}&program_category=Industry`,
      );
      const videoItems: { video_id: string; video_title: string }[] = [];
      if (response.data?.videos && Array.isArray(response.data.videos)) {
        response.data.videos.forEach((sub: any) => {
          if (sub.list_videos && Array.isArray(sub.list_videos)) {
            sub.list_videos.forEach((v: any) => {
              videoItems.push({
                video_id: v.video_id,
                video_title: v.video_title || v.file_title || "Untitled Video",
              });
            });
          }
        });
      }
      setVideosList(videoItems);
      return videoItems;
    } catch (error) {
      Logger.error("Error fetching videos for gallery:", error);
      setVideosList([]);
      return [];
    } finally {
      setLoadingVideos(false);
    }
  };

  const isFromLearningRoom = Boolean(navState?.fromLearningRoomVideo);

  // Auto-sync active program or handle learning room navigation cascade
  useEffect(() => {
    const initCascade = async () => {
      const targetProgId =
        navState?.program_id ||
        navState?.programId ||
        selectedProgramId ||
        listProgramData[0]?.program_id ||
        "";
      const targetSubjId =
        navState?.subject_id ||
        navState?.subjectId ||
        navState?.course_id ||
        "";
      const targetTopicId = navState?.topic_id || navState?.topicId || "";
      const targetVideoId = navState?.video_id || navState?.videoId || "";

      if (!targetProgId && listProgramData.length === 0) return;

      const progObj = listProgramData.find(
        (p: any) => String(p.program_id) === String(targetProgId),
      );
      const progTitle =
        progObj?.program_title ||
        navState?.program_name ||
        navState?.programName ||
        filter ||
        listProgramData[0]?.program_title ||
        "";

      if (targetProgId) {
        setSelectedProgramState({ id: targetProgId, title: progTitle });
      }

      if (targetProgId) {
        const fetchedSubjects = await fetchSubjects(targetProgId);
        if (targetSubjId) {
          const subjObj = fetchedSubjects.find(
            (s: any) => String(s.course_id) === String(targetSubjId),
          );
          const subjTitle =
            subjObj?.course_title ||
            navState?.subject_name ||
            navState?.subjectName ||
            navState?.course_name ||
            "";
          setSelectedSubject({ id: targetSubjId, title: subjTitle });

          const fetchedTopics = await fetchTopics(targetProgId, targetSubjId);
          if (targetTopicId) {
            const topicObj = fetchedTopics.find(
              (t: any) => String(t.topic_id) === String(targetTopicId),
            );
            const topicTitle =
              topicObj?.topic_title ||
              navState?.topic_name ||
              navState?.topicName ||
              "";
            setSelectedTopic({ id: targetTopicId, title: topicTitle });

            const fetchedVideos = await fetchVideos(
              targetProgId,
              targetSubjId,
              targetTopicId,
            );
            if (targetVideoId) {
              const videoObj = fetchedVideos.find(
                (v: any) => String(v.video_id) === String(targetVideoId),
              );
              const videoTitle =
                videoObj?.video_title ||
                navState?.video_name ||
                navState?.videoName ||
                "";
              setSelectedVideoState({ id: targetVideoId, title: videoTitle });
            }
          }
        }
      }
    };

    initCascade();
  }, [listProgramData, selectedProgramId, location.state]);

  const handleViewFeedback = (
    feedback: string | undefined,
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();

    setSelectedFeedback(feedback || "No feedback available.");
    setFeedbackModalOpen(true);
  };

  const getProgram = async () => {
    Loader.showLoading("Loading...");

    api.setAuthToken(LocalStorage.getLocalData("access_token"));

    const institute_id = LocalStorage.getLocalData("institute_id");

    try {
      const response = await api.get<any>(`/eprogram/${institute_id}`);

      if (response.data.programs?.length > 0) {
        dispatch(setPrograms(response.data.programs));

        const targetProgId = navState?.program_id || navState?.programId;
        const matchedProgram = targetProgId
          ? response.data.programs.find(
            (p: any) => String(p.program_id) === String(targetProgId),
          )
          : null;

        const selectedProgram = matchedProgram || response.data.programs[0];

        dispatch(
          setSelectedValues([
            {
              program_title: selectedProgram.program_title,
              program_id: selectedProgram.program_id,
            },
          ]),
        );

        setFilter(selectedProgram.program_title);
      }

      if (response.data.error) {
        dispatch(setPrograms([]));
        return;
      }

      if (response.data.programs && response.data.programs.length > 0) {
        dispatch(setPrograms(response.data.programs));
      } else {
        dispatch(setPrograms([]));
      }
    } catch (error) {
      Logger.info("Program API Error", error);
    } finally {
      Loader.dismissLoading();
    }
  };

  const handleOptionClick = (program: {
    program_title: string;
    program_id: string;
  }) => {
    setFilter(program.program_title);
    setIsDropdownOpen(false);

    dispatch(
      setSelectedValues([
        {
          program_title: program.program_title,
          program_id: program.program_id,
        },
      ]),
    );

    // Optional:
    // call gallery API with selected program
    // getGalleryData(program.program_id);
  };
  // Retrieve user metadata from Redux store
  const profileData = useSelector(
    (state: RootState) => state.profile.profileKeys,
  );
  const userName = useMemo(() => {
    if (profileData && (profileData.first_name || profileData.last_name)) {
      return `${profileData.first_name || ""} ${profileData.last_name || ""}`.trim();
    }
    return "Learner";
  }, [profileData]);

  const [galleryData, setGalleryData] = useState<GalleryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMedia, setSelectedMedia] = useState<GalleryItem | null>(null);

  // Generate a mock S3 Pre-signed URL
  const generateMockS3PreSignedUrl = (fileName: string, type: MediaType) => {
    const bucketName = "jozuna-learner-practices";
    const sanitizedName = userName.replace(/\s+/g, "_").toLowerCase();
    const folder = type === "image" ? "images" : "videos";
    const expirationTime = Math.floor(Date.now() / 1000) + 3600 * 24; // 24-hour pre-signed window

    return `https://${bucketName}.s3.amazonaws.com/${sanitizedName}/${folder}/${fileName}?X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=AKIAIOSFODNN7EXAMPLE%2F20260611%2Fus-east-1%2Fs3%2Faws4_request&X-Amz-Date=20260611T212000Z&X-Amz-Expires=${expirationTime}&X-Amz-SignedHeaders=host&X-Amz-Signature=mockSignature5ef4e0a7f14b62d87e07a3`;
  };

  const addMedia = (file: File, type: MediaType, s3Url?: string) => {
    // Generate object URL for immediate browser playback / layout
    const blobUrl = URL.createObjectURL(file);
    const finalS3Url = s3Url || generateMockS3PreSignedUrl(file.name, type);

    const mediaItem: GalleryMedia = {
      id: Date.now().toString(),
      type,
      url: blobUrl,
      s3Url: finalS3Url,
      fileName: file.name,
      createdAt: new Date().toISOString(),
      author: userName,
    };

    setActiveTab("gallery");
  };

  const deleteMedia = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    showAlert({
      // identity: "default",
      show: true,
      title: "Info",
      message: "Are you sure you want delete this media ?",
      button1Text: "yes",
      button2Text: "No",
      alertType: "info",
      onlyNeedOneButton: false,
      btn1onPressed: () => {
        deleteMediaDetail(id);
      },
      btn2onPressed: () => { },
    });
  };

  const deleteMediaDetail = async (gallery_id: string) => {
    api.setAuthToken(LocalStorage.getLocalData("access_token"));
    try {
      const res = await api
        .delete<{
          detail: string;
          error: string;
        }>(
          `/media_delete/?gallery_ids=${gallery_id}&program_id=${selectedProgramId}`,
        )
        .then((res) => {
          getGalleryData(selectedProgramId);

          showAlert({
            // identity: "default",
            show: true,
            title: "Success",
            message: res.data.detail,
            button1Text: "Ok",
            alertType: "info",
            onlyNeedOneButton: true,
            btn1onPressed: () => { },
          });
        })
        .catch((e) => {
          showAlert({
            // identity: "default",
            show: true,
            title: "Error",
            message: e,
            button1Text: "Ok",
            alertType: "error",
            onlyNeedOneButton: true,
            btn1onPressed: () => { },
          });
        });
    } catch { }
  };

  const getRemainingDays = (uploadedAt: string) => {
    const formatted = uploadedAt.replace("|", "");
    const uploadedDate = new Date(formatted);

    if (isNaN(uploadedDate.getTime())) {
      return 0;
    }

    const diffDays = Math.floor(
      (Date.now() - uploadedDate.getTime()) / (1000 * 60 * 60 * 24),
    );

    return Math.max(0, 30 - diffDays);
  };

  // Determine expiration badge class for styling
  const getRemainingDaysClass = (daysLeft: number) => {
    if (daysLeft >= 25) return "days_badge safe";
    if (daysLeft >= 10) return "days_badge warning";
    return "days_badge critical";
  };

  const formatLocalTime = (dateString: any) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  };

  const api = useApi();
  const getGalleryData = async (programId: string) => {
    try {
      setLoading(true);

      api.setAuthToken(LocalStorage.getLocalData("access_token"));

      const res = await api.get<GalleryResponse>(
        `/gallery?program_id=${programId}`,
      );

      if (res?.data) {
        setGalleryData(res.data.media);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    getProgram();
  }, []);

  useEffect(() => {
    if (activeTab === "gallery" && selectedProgramId) {
      getGalleryData(selectedProgramId);
    }
  }, [activeTab, selectedProgramId]);

  const months: Record<string, number> = {
    Jan: 0,
    Feb: 1,
    Mar: 2,
    Apr: 3,
    May: 4,
    Jun: 5,
    Jul: 6,
    Aug: 7,
    Sep: 8,
    Oct: 9,
    Nov: 10,
    Dec: 11,
  };

  const convertUTCToLocal = (dateStr: string) => {
    const [datePart, timePart] = dateStr.split("|").map((s) => s.trim());

    const [day, month, year] = datePart.split(" ");
    let [time, period] = timePart.split(" ");
    let [hour, minute, second] = time.split(":").map(Number);

    if (period === "PM" && hour !== 12) hour += 12;
    if (period === "AM" && hour === 12) hour = 0;

    const utcDate = new Date(
      Date.UTC(Number(year), months[month], Number(day), hour, minute, second),
    );

    return utcDate.toLocaleString("en-IN");
  };
  return (
    <div className="gallery_page">
      {/* Header section */}
      <div
        className="v2-dashboard-header"
        style={
          {
            // paddingBottom: "20px",
          }
        }
      >
        <div
          style={{
            display: "flex",
            alignItems: "start",
            justifyContent: "space-between",
            boxShadow: "0 4px 8px rgba(105, 103, 103, 0.1)", // Bottom box shadow
            padding: "20px",
            height: "12vh",
            zIndex: 10000,
            marginBottom: "10px",
          }}
        >
          <div
            style={{
              color: "#121212",
              // marginLeft: "20px",
              fontWeight: "500",
              fontFamily: "Mazzard",
              fontSize: "20px",
              display: "flex",
              alignItems: "center",
              // marginRight: "20px", // Adjust margin to provide space after the welcome message
            }}
          >
            <div>
              Gallery
              <div
                style={{
                  color: "#000000",
                  //   marginLeft: "20px",
                  fontWeight: "300",
                  fontFamily: "Mazzard",
                  fontSize: "14px",
                  marginTop: "4px",
                  //   marginRight: "20px", // Adjust margin to provide space after the welcome message
                }}
              >
                Review and download your recorded hands-on sessions.
              </div>
            </div>
          </div>

          {activeTab === "gallery" && (
            <div className="gallery-CustomDropdown">
              <div
                className="gallery-CustomDropdownSelected"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              >
                <EllipsisTooltip
                  text={filter}
                  className="gallery-dropdown-text"
                />

                <img
                  src={dropdownArrow}
                  alt="Dropdown Arrow"
                  className="gallery-DropdownArrowImg"
                />
              </div>

              {isDropdownOpen && (
                <ul className="gallery-CustomDropdownOptions">
                  {listProgramData.length === 0 ? (
                    <li
                      className="gallery-DropdownOption"
                      style={{
                        color: "#999",
                        cursor: "default",
                      }}
                    >
                      No Programs Found
                    </li>
                  ) : (
                    <>
                      {listProgramData.map((program) => (
                        <li
                          key={program.program_id}
                          className="gallery-DropdownOption"
                          onClick={() => handleOptionClick(program)}
                        >
                          {program.program_title}
                        </li>
                      ))}
                    </>
                  )}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
      {/* <div className="gallery_page_Divider" /> */}
      <div style={{ padding: "16px 20px", flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>



            <div className="gallery_tabs">
              <button
                className={activeTab === "gallery" ? "active" : ""}
                onClick={() => setActiveTab("gallery")}
              >
                📂 Gallery Media ({galleryData.length})
              </button>

              <button
                className={activeTab === "capture" ? "active" : ""}
                onClick={() => setActiveTab("capture")}
              >
                📸 Capture Media
              </button>
            </div>

            {isFromLearningRoom && (
              <button
                onClick={() => navigate(-1)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "10px 16px",
                  backgroundColor: "#ffffff",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  color: "#1e293b",
                  fontWeight: 500,
                  fontSize: "14px",
                  cursor: "pointer",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                  marginLeft: "16px",
                  marginBottom: "20px",
                  transition: "background-color 0.2s, box-shadow 0.2s",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = "#f8fafc";
                  e.currentTarget.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.1)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = "#ffffff";
                  e.currentTarget.style.boxShadow = "0 1px 3px rgba(0, 0, 0, 0.1)";
                }}
                title="Go back to video"
              >
                <img
                  src={backArrowImg}
                  alt="Back"
                  style={{ width: "18px", height: "18px" }}
                />
                Back to Video
              </button>
            )}
          </div>

          {/* Capture Option Toggle */}
          {activeTab === "capture" && (
            <div className="capture_option_selector">
              <button
                className={captureMode === "image" ? "active" : ""}
                onClick={() => setCaptureMode("image")}
              >
                📷 Image Capture
              </button>
              <button
                className={captureMode === "video" ? "active" : ""}
                onClick={() => setCaptureMode("video")}
              >
                🎥 Video Recording
              </button>
              <button
                className={captureMode === "screen" ? "active" : ""}
                onClick={() => setCaptureMode("screen")}
              >
                🖥️ Screen Recording
              </button>
            </div>
          )}
        </div>{" "}
        {/* Modern Tabs controls */}
        {/* Scrollable Content Container */}
        <div className="gallery_tab_content">
          {/* {navState?.fromLearningRoomVideo && activeTab !== "gallery" && (
            <div
              className="learning-room-context-banner"
              style={{
                backgroundColor: "#e0f2fe",
                border: "1px solid #bae6fd",
                color: "#0369a1",
                padding: "10px 16px",
                borderRadius: "8px",
                marginBottom: "16px",
                fontSize: "14px",
                fontFamily: "Mazzard, sans-serif",
                display: "flex",
                flexWrap: "wrap",
                gap: "12px",
                alignItems: "center",
              }}
            >
              <span>
                <strong>Learning Room Context:</strong>
              </span>
              {(navState.program_name || navState.programName) && (
                <span>
                  <strong>Program:</strong>{" "}
                  {navState.program_name || navState.programName}
                </span>
              )}
              {(navState.subject_name ||
                navState.subjectName ||
                navState.course_name) && (
                  <span>
                    <strong>Subject:</strong>{" "}
                    {navState.subject_name ||
                      navState.subjectName ||
                      navState.course_name}
                  </span>
                )}
              {(navState.topic_name || navState.topicName) && (
                <span>
                  <strong>Topic:</strong>{" "}
                  {navState.topic_name || navState.topicName}
                </span>
              )}
              {(navState.video_name || navState.videoName) && (
                <span>
                  <strong>Video:</strong>{" "}
                  {navState.video_name || navState.videoName}
                </span>
              )}
            </div>
          )} */}
          {/* GALLERY DISPLAY TAB */}
          {activeTab === "gallery" && (
            <>
              <div className="gallery_info_banner">
                <span className="gallery_info_banner_icon">📌</span>
                <div>
                  <strong>Note:</strong> Captured Medias are stored securely and
                  will be available for <strong>30 days</strong> from the
                  capture date.
                </div>
              </div>

              {galleryData.length === 0 ? (
                <div className="gallery_empty_state">
                  <svg
                    width="120"
                    height="120"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#cbd5e1"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ marginBottom: "8px" }}
                  >
                    <path d="M21 19V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2z"></path>
                    <circle cx="8.5" cy="8.5" r="1.5"></circle>
                    <polyline points="21 15 16 10 5 21"></polyline>
                  </svg>
                  <h3>No Media Available</h3>
                  <p>
                    You haven't captured any Media yet. Grant camera access and
                    record your first Media session or take a snapshot.
                  </p>
                  <button
                    className="primary_btn"
                    onClick={() => {
                      setActiveTab("capture");
                      setCaptureMode("image");
                    }}
                  >
                    Capture First Media
                  </button>
                </div>
              ) : (
                <div className="gallery_grid">
                  {galleryData.map((media) => {
                    const daysLeft = getRemainingDays(media.uploaded_at);

                    return (
                      <div
                        key={media.gallery_id}
                        className="gallery_card"
                        onClick={() => setSelectedMedia(media)}
                      >
                        <div className="gallery_thumbnail">
                          {/* Media badge indicator */}
                          <span className="media_type_badge">
                            {media.media_type === "image" ? "📷" : "🎥"}
                          </span>

                          {/* Render preview */}
                          {media.media_type === "image" ? (
                            <img src={media.media_url} alt="" loading="lazy" />
                          ) : (
                            <>
                              <video src={media.media_url} muted playsInline />
                              <div className="video_play_overlay">
                                <span className="play_button_circle">▶</span>
                              </div>
                            </>
                          )}
                        </div>

                        <div className="gallery_footer">
                          <div className="media_meta_row">
                            <div>
                              <div
                                className="media_name"
                                title={media.file_name}
                              >
                                {media.file_name}
                              </div>

                              <div className="media_date">
                                {convertUTCToLocal(media.uploaded_at)}
                                {/* {new Date(media.uploaded_at).toLocaleString()} */}
                              </div>
                              <div>
                                {" "}
                                <div
                                  className={`media_source_tag ${media.uploaded_by_user
                                    ? "user_tag"
                                    : "institute_tag"
                                    }`}
                                >
                                  {media.uploaded_by_user
                                    ? "User"
                                    : "Instructor"}
                                </div>
                                {media.uploaded_by_user === true && (
                                  <button
                                    onClick={(e) =>
                                      deleteMedia(media.gallery_id, e)
                                    }
                                    style={{
                                      background: "none",
                                      border: "none",
                                      color: "#a7a4a4",
                                      cursor: "pointer",
                                      fontSize: "14px",
                                      alignSelf: "flex-end",
                                      fontFamily: "Mazzard",
                                      marginTop: "-8px",
                                      padding: "4px 8px",
                                      borderRadius: "6px",
                                      transition: "all 0.2s",
                                    }}
                                    onMouseEnter={(e) =>
                                      (e.currentTarget.style.color = "#ef4444")
                                    }
                                    onMouseLeave={(e) =>
                                      (e.currentTarget.style.color = "#94a3b8")
                                    }
                                  >
                                    🗑 Delete
                                  </button>
                                )}
                              </div>
                            </div>

                            <div className={getRemainingDaysClass(daysLeft)}>
                              {daysLeft} {daysLeft === 1 ? "day" : "days"} left
                            </div>
                          </div>

                          <div className="media_author">
                            <div className="author_avatar_mini">
                              {media.uploaded_by.charAt(0).toUpperCase()}
                            </div>
                            <span>By {media.uploaded_by}</span>
                          </div>

                          {/* Delete icon */}

                          {media.feedback_message !== null && (
                            <div className="gallery_card_actions">
                              <button
                                className="view-feedback-btn"
                                onClick={(e) =>
                                  handleViewFeedback(media.feedback_message, e)
                                }
                              >
                                View Feedback
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {feedbackModalOpen && (
            <div
              className="feedback-modal-overlay"
              onClick={() => setFeedbackModalOpen(false)}
            >
              <div
                className="feedback-modal"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="feedback-modal-header">
                  <h3>Feedback</h3>

                  <button onClick={() => setFeedbackModalOpen(false)}>✕</button>
                </div>

                <div className="feedback-modal-body">{selectedFeedback}</div>
              </div>
            </div>
          )}

          {/* CAPTURE MEDIA TAB */}
          {activeTab === "capture" && (
            <div className="capture_layout">
              {/* Left Side: 4-Level Hierarchy Sidebar */}
              <div className="hierarchy_sidebar">
                <div className="hierarchy_sidebar_title">
                  <span>📋</span> Assignment Selection
                </div>
                {/* <div style={{ fontSize: "12px", color: "#64748b" }}>
                  Select Program, Subject, Topic, and Video to enable media capture.
                </div> */}

                {/* 1. Select Program */}
                <div className="hierarchy_field">
                  <label className="hierarchy_label">
                    1. Select Program {!isFromLearningRoom && <span style={{ color: "#64748b", fontWeight: "normal", fontSize: "12px" }}>(Optional)</span>}
                  </label>
                  <div className="hierarchy_custom_dropdown">
                    <div
                      className="hierarchy_custom_selected"
                      onClick={() =>
                        setOpenHierarchyDrop(
                          openHierarchyDrop === "program" ? null : "program",
                        )
                      }
                    >
                      <EllipsisTooltip
                        text={selectedProgramState.title || "Select Program"}
                        className="gallery-dropdown-text"
                      />
                      <img
                        src={dropdownArrow}
                        alt="Dropdown Arrow"
                        className="gallery-DropdownArrowImg"
                      />
                    </div>

                    {openHierarchyDrop === "program" && (
                      <ul className="hierarchy_custom_options">
                        {listProgramData.length === 0 ? (
                          <li
                            className="hierarchy_custom_option"
                            style={{ color: "#999", cursor: "default" }}
                          >
                            No Programs Found
                          </li>
                        ) : (
                          listProgramData.map((p) => (
                            <li
                              key={p.program_id}
                              className={`hierarchy_custom_option ${selectedProgramState.id === p.program_id ? "selected" : ""}`}
                              onClick={async () => {
                                setOpenHierarchyDrop(null);
                                setSelectedProgramState({
                                  id: p.program_id,
                                  title: p.program_title,
                                });
                                setSelectedSubject({ id: "", title: "" });
                                setSubjectsList([]);
                                setSelectedTopic({ id: "", title: "" });
                                setTopicsList([]);
                                setSelectedVideoState({ id: "", title: "" });
                                setVideosList([]);
                                await fetchSubjects(p.program_id);
                              }}
                            >
                              {p.program_title}
                            </li>
                          ))
                        )}
                      </ul>
                    )}
                  </div>
                </div>

                {/* 2. Select Subject */}
                <div className="hierarchy_field">
                  <label className="hierarchy_label">
                    2. Select Subject {!isFromLearningRoom && <span style={{ color: "#64748b", fontWeight: "normal", fontSize: "12px" }}>(Optional)</span>}
                  </label>
                  <div className="hierarchy_custom_dropdown">
                    <div
                      className={`hierarchy_custom_selected ${!selectedProgramState.id ? "disabled" : ""}`}
                      onClick={() => {
                        if (selectedProgramState.id) {
                          setOpenHierarchyDrop(
                            openHierarchyDrop === "subject" ? null : "subject",
                          );
                        }
                      }}
                    >
                      <EllipsisTooltip
                        text={selectedSubject.title || "Select Subject"}
                        className="gallery-dropdown-text"
                      />
                      <img
                        src={dropdownArrow}
                        alt="Dropdown Arrow"
                        className="gallery-DropdownArrowImg"
                      />
                    </div>

                    {openHierarchyDrop === "subject" &&
                      selectedProgramState.id && (
                        <ul className="hierarchy_custom_options">
                          {subjectsList.length === 0 ? (
                            <li
                              className="hierarchy_custom_option"
                              style={{ color: "#999", cursor: "default" }}
                            >
                              No Subjects Found
                            </li>
                          ) : (
                            subjectsList.map((s) => (
                              <li
                                key={s.course_id}
                                className={`hierarchy_custom_option ${selectedSubject.id === s.course_id ? "selected" : ""}`}
                                onClick={async () => {
                                  setOpenHierarchyDrop(null);
                                  setSelectedSubject({
                                    id: s.course_id,
                                    title: s.course_title,
                                  });
                                  setSelectedTopic({ id: "", title: "" });
                                  setTopicsList([]);
                                  setSelectedVideoState({ id: "", title: "" });
                                  setVideosList([]);
                                  await fetchTopics(
                                    selectedProgramState.id,
                                    s.course_id,
                                  );
                                }}
                              >
                                {s.course_title}
                              </li>
                            ))
                          )}
                        </ul>
                      )}
                  </div>
                </div>

                {/* 3. Select Topic */}
                <div className="hierarchy_field">
                  <label className="hierarchy_label">
                    3. Select Topic {!isFromLearningRoom && <span style={{ color: "#64748b", fontWeight: "normal", fontSize: "12px" }}>(Optional)</span>}
                  </label>
                  <div className="hierarchy_custom_dropdown">
                    <div
                      className={`hierarchy_custom_selected ${!selectedSubject.id ? "disabled" : ""}`}
                      onClick={() => {
                        if (selectedSubject.id) {
                          setOpenHierarchyDrop(
                            openHierarchyDrop === "topic" ? null : "topic",
                          );
                        }
                      }}
                    >
                      <EllipsisTooltip
                        text={selectedTopic.title || "Select Topic"}
                        className="gallery-dropdown-text"
                      />
                      <img
                        src={dropdownArrow}
                        alt="Dropdown Arrow"
                        className="gallery-DropdownArrowImg"
                      />
                    </div>

                    {openHierarchyDrop === "topic" && selectedSubject.id && (
                      <ul className="hierarchy_custom_options">
                        {topicsList.length === 0 ? (
                          <li
                            className="hierarchy_custom_option"
                            style={{ color: "#999", cursor: "default" }}
                          >
                            No Topics Found
                          </li>
                        ) : (
                          topicsList.map((t) => (
                            <li
                              key={t.topic_id}
                              className={`hierarchy_custom_option ${selectedTopic.id === t.topic_id ? "selected" : ""}`}
                              onClick={async () => {
                                setOpenHierarchyDrop(null);
                                setSelectedTopic({
                                  id: t.topic_id,
                                  title: t.topic_title,
                                });
                                setSelectedVideoState({ id: "", title: "" });
                                setVideosList([]);
                                await fetchVideos(
                                  selectedProgramState.id,
                                  selectedSubject.id,
                                  t.topic_id,
                                );
                              }}
                            >
                              {t.topic_title}
                            </li>
                          ))
                        )}
                      </ul>
                    )}
                  </div>
                </div>

                {/* 4. Select Video */}
                <div className="hierarchy_field">
                  <label className="hierarchy_label">
                    4. Select Video {!isFromLearningRoom && <span style={{ color: "#64748b", fontWeight: "normal", fontSize: "12px" }}>(Optional)</span>}
                  </label>
                  <div className="hierarchy_custom_dropdown">
                    <div
                      className={`hierarchy_custom_selected ${!selectedTopic.id ? "disabled" : ""}`}
                      onClick={() => {
                        if (selectedTopic.id) {
                          setOpenHierarchyDrop(
                            openHierarchyDrop === "video" ? null : "video",
                          );
                        }
                      }}
                    >
                      <EllipsisTooltip
                        text={selectedVideo.title || "Select Video"}
                        className="gallery-dropdown-text"
                      />
                      <img
                        src={dropdownArrow}
                        alt="Dropdown Arrow"
                        className="gallery-DropdownArrowImg"
                      />
                    </div>

                    {openHierarchyDrop === "video" && selectedTopic.id && (
                      <ul className="hierarchy_custom_options">
                        {videosList.length === 0 ? (
                          <li
                            className="hierarchy_custom_option"
                            style={{ color: "#999", cursor: "default" }}
                          >
                            No Videos Found
                          </li>
                        ) : (
                          videosList.map((v) => (
                            <li
                              key={v.video_id}
                              className={`hierarchy_custom_option ${selectedVideo.id === v.video_id ? "selected" : ""}`}
                              onClick={() => {
                                setOpenHierarchyDrop(null);
                                setSelectedVideoState({
                                  id: v.video_id,
                                  title: v.video_title,
                                });
                              }}
                            >
                              {v.video_title}
                            </li>
                          ))
                        )}
                      </ul>
                    )}
                  </div>
                </div>
              </div>

              {/* Right Side: Main Capture Area */}
              <div className="capture_main_area">
                {/* {!selectedVideo.id && (
                  <div className="capture_disabled_overlay_badge">
                    <span>⚠️</span>
                    <span>
                      Please complete selection (Program ➔ Subject ➔ Topic ➔ Video) on the left to enable Media Capture.
                    </span>
                  </div>
                )} */}
                {captureMode === "image" ? (
                  <ImageCapture
                    onSubmit={(file: File, s3Url?: string) =>
                      addMedia(file, "image", s3Url)
                    }
                    mediaType="image"
                    disabled={isFromLearningRoom ? !selectedVideo.id : false}
                    program_id={selectedProgramState.id}
                    subject_id={selectedSubject.id}
                    topic_id={selectedTopic.id}
                    video_id={selectedVideo.id}
                    program_name={selectedProgramState.title}
                    subject_name={selectedSubject.title}
                    topic_name={selectedTopic.title}
                    video_name={selectedVideo.title}
                  />
                ) : captureMode === "video" ? (
                  <VideoRecorder
                    onSubmit={(file: File, s3Url?: string) =>
                      addMedia(file, "video", s3Url)
                    }
                    mediaType="video"
                    disabled={isFromLearningRoom ? !selectedVideo.id : false}
                    program_id={selectedProgramState.id}
                    subject_id={selectedSubject.id}
                    topic_id={selectedTopic.id}
                    video_id={selectedVideo.id}
                    program_name={selectedProgramState.title}
                    subject_name={selectedSubject.title}
                    topic_name={selectedTopic.title}
                    video_name={selectedVideo.title}
                  />
                ) : (
                  <ScreenRecorder
                    onSubmit={(file: File, s3Url?: string) =>
                      addMedia(file, "video", s3Url)
                    }
                    mediaType="video"
                    disabled={isFromLearningRoom ? !selectedVideo.id : false}
                    program_id={selectedProgramState.id}
                    subject_id={selectedSubject.id}
                    topic_id={selectedTopic.id}
                    video_id={selectedVideo.id}
                    program_name={selectedProgramState.title}
                    subject_name={selectedSubject.title}
                    topic_name={selectedTopic.title}
                    video_name={selectedVideo.title}
                  />
                )}
              </div>
            </div>
          )}
        </div>
        {/* LIGHTBOX PREVIEW MODAL */}
        {selectedMedia && (
          <div
            className="media_modal_overlay"
            onClick={() => setSelectedMedia(null)}
          >
            <div className="media_modal" onClick={(e) => e.stopPropagation()}>
              <div className="media_modal_header">
                <h3>{selectedMedia.file_name}</h3>
                <button onClick={() => setSelectedMedia(null)}>✕</button>
              </div>

              <div className="media_modal_body">
                {selectedMedia.media_type === "image" ? (
                  <img
                    src={selectedMedia.media_url}
                    alt=""
                    className="preview_media"
                  />
                ) : (
                  <video
                    key={selectedMedia.gallery_id || selectedMedia.media_url}
                    src={selectedMedia.media_url}
                    controls
                    autoPlay
                    preload="auto"
                    playsInline
                    className="preview_media"
                    onLoadedMetadata={(e) => {
                      const vid = e.currentTarget;
                      if (vid.duration === Infinity || isNaN(vid.duration)) {
                        vid.currentTime = 1e101;
                        vid.ontimeupdate = () => {
                          vid.ontimeupdate = null;
                          vid.currentTime = 0;
                          vid.play().catch(() => {});
                        };
                      } else {
                        vid.play().catch(() => {});
                      }
                    }}
                  >
                    Your browser does not support video playbacks.
                  </video>
                )}
              </div>

              <div className="media_modal_footer">
                <div className="modal_meta_info">
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#1e293b",
                    }}
                  >
                    Uploaded by {selectedMedia.uploaded_by}
                  </div>
                  <div className="modal_meta_time">
                    Captured: {convertUTCToLocal(selectedMedia.uploaded_at)}
                  </div>
                  {/* Print simulated pre-signed S3 URL details for debugging/backend inspection */}
                  {/* <div
                  style={{
                    fontSize: "9px",
                    fontFamily: "monospace",
                    color: "#64748b",
                    wordBreak: "break-all",
                    maxWidth: "450px",
                    marginTop: "4px",
                  }}
                >
                  S3 Link: {selectedMedia.s3Url.substring(0, 100)}...
                </div> */}
                </div>

                <a
                  href={selectedMedia.media_url}
                  download={selectedMedia.file_name}
                  className="download_btn"
                >
                  📥 Download Media
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GalleryPage;


//new code has been added and successfully completed  added values ///